import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Megaphone, ShieldCheck } from 'lucide-react';
import type { OwnerReleaseDto } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, DatePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { OWNER_RELEASES_KEY, VERSION_PATTERN } from './OwnerReleasesPage';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function AnnounceReleasePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/owner/releases');
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [requiredBy, setRequiredBy] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const announce = useMutation({
    mutationFn: () =>
      post<OwnerReleaseDto>('/owner/releases/announce', {
        version,
        channel: 'STABLE',
        notes,
        isRequired,
        ...(isRequired && requiredBy ? { requiredBy: new Date(`${requiredBy}T23:59:00`).toISOString() } : {}),
        totpCode,
      }),
    onSuccess: () => {
      toast.success(`Version ${version} announced`, 'Upload its package when it is ready; servers are offered it only then.');
      void queryClient.invalidateQueries({ queryKey: OWNER_RELEASES_KEY });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => {
      setError(describeError(caught));
      toast.error('Could not announce the version', describeError(caught));
    },
  });

  const versionInvalid = version !== '' && !VERSION_PATTERN.test(version);
  const ready = Boolean(version) && !versionInvalid && isValidTotp(totpCode);
  const dirty = !announce.isSuccess && (version !== '' || notes !== '' || isRequired || requiredBy !== '' || totpCode !== '');

  return (
    <FormScreen
      title="Announce a version"
      description="Tell facilities a version is coming — and, if it is required, by when — before its package is ready."
      icon={Megaphone}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        announce.mutate();
      }}
    >
      <FormSection title="Version" icon={Megaphone}>
        <Field label="Version" htmlFor="announce-version" required hint={versionInvalid ? 'Use the form 1.4.0 or 1.4.0-beta.1' : undefined}>
          <Input id="announce-version" className="font-mono" value={version} onChange={(e) => setVersion(e.target.value.trim())} />
        </Field>
        <Checkbox className="sm:pt-6" checked={isRequired} onCheckedChange={setIsRequired} label="Required" />
        {isRequired && (
          <Field label="Required by" htmlFor="announce-required-by" hint="Leave empty for 14 days from today.">
            <DatePicker id="announce-required-by" value={requiredBy} onChange={(value) => setRequiredBy(value)} />
          </Field>
        )}
        <Field label="Notes" htmlFor="announce-notes" className="sm:col-span-2">
          <Textarea id="announce-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions submitLabel="Announce" loading={announce.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
